import React, { useState, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import styles from '../../../Configurations.module.css';
import componentStyles from './Matrices.module.css';
import matrixService from '../../../../services/matrixService';
import { useAlert } from '../../../../context/AlertContext';
import { useAccessToken } from '../../../../hooks/useAuth';
import MatrixForm from './MatrixForm';
import MatrixList from './MatrixList';
import MatrixStatistics from './MatrixStatistics';

const Matrices = () => {
    const { setErrorAlert, setInfoAlert, setIsModalOpen } = useAlert();
    const { getBackendAccessToken } = useAccessToken();
    const [matrices, setMatrices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statistics, setStatistics] = useState({
        total: 0,
        active: 0,
        liquid: 0,
        solid: 0,
        gas: 0
    });

    const [showForm, setShowForm] = useState(false);
    const [editingMatrix, setEditingMatrix] = useState(null);

    useEffect(() => {
        loadMatrices();
    }, []);

    // Track modal state for alert positioning and header blocking
    useEffect(() => {
        if (showForm) {
            document.body.setAttribute('data-modal-open', 'true');
            setIsModalOpen(true);
        } else {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        }
        return () => {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        };
    }, [showForm, setIsModalOpen]);

    const loadMatrices = async () => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            const data = await matrixService.getAll(accessToken);
            
            if (Array.isArray(data)) {
                setMatrices(data);
                setError(null);
                // Update statistics
                const stats = {
                    total: data.length,
                    active: data.filter(m => m.active).length,
                    liquid: data.filter(m => m.matrixType === 'Liquid').length,
                    solid: data.filter(m => m.matrixType === 'Solid').length,
                    gas: data.filter(m => m.matrixType === 'Gas').length
                };
                setStatistics(stats);
            } else {
                throw new Error(`Invalid response format. Expected array, got ${typeof data}`);
            }
        } catch (err) {
            const errorMessage = 'Failed to load matrices. Please try again later.';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            console.error('Error loading matrices:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            const accessToken = await getBackendAccessToken();
            if (editingMatrix) {
                await matrixService.update(accessToken, editingMatrix.matrixId, formData);
                setInfoAlert('Matrix updated successfully');
            } else {
                await matrixService.create(accessToken, formData);
                setInfoAlert('Matrix created successfully');
            }
            await loadMatrices();
            setShowForm(false);
            setEditingMatrix(null);
            setError(null);
        } catch (err) {
            setError(err.message || 'Operation failed');
            setErrorAlert(err.message || 'Operation failed');
            console.error('Error handling matrix:', err);
        }
    };

    const handleEdit = (matrix) => {
        setEditingMatrix(matrix);
        setShowForm(true);
    };

    const handleDelete = async (matrixId) => {
        try {
            const accessToken = await getBackendAccessToken();
            await matrixService.delete(accessToken, matrixId);
            await loadMatrices();
            setInfoAlert('Matrix deleted successfully');
            setError(null);
        } catch (err) {
            const errorMsg = 'Failed to delete matrix. Please try again.';
            setError(errorMsg);
            setErrorAlert(errorMsg);
            console.error('Error deleting matrix:', err);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingMatrix(null);
    };

    const filteredMatrices = matrices.filter(matrix => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            matrix.matrix.toLowerCase().includes(query) ||
            (matrix.description && matrix.description.toLowerCase().includes(query)) ||
            (matrix.matrixType && matrix.matrixType.toLowerCase().includes(query))
        );
    });

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2>Matrices</h2>
                <p>Manage laboratory sample matrices (physical/chemical composition or form)</p>
            </div>

            <div className={styles.content}>
                {/* Statistics Section */}
                {!loading && !error && (
                    <MatrixStatistics 
                        statistics={statistics}
                        loading={loading}
                    />
                )}

                {error && (
                    <div className={componentStyles.errorMessage}>
                        <span className={componentStyles.errorIcon}>⚠</span>
                        {error}
                    </div>
                )}

                {/* Controls Section */}
                <div className={componentStyles.controls}>
                    <button
                        onClick={() => setShowForm(true)}
                        className={componentStyles.addButton}
                        disabled={showForm || loading}
                    >
                        <FaPlus className={componentStyles.buttonIcon} /> Create New Matrix
                    </button>
                </div>

                {/* Form Modal */}
                {showForm && (
                    <div className={componentStyles.modal}>
                        <div className={componentStyles.modalContent}>
                            <MatrixForm
                                matrix={editingMatrix}
                                onSubmit={handleFormSubmit}
                                onCancel={handleCancel}
                                loading={loading}
                            />
                        </div>
                    </div>
                )}

                {/* Matrices List */}
                <MatrixList
                    matrices={filteredMatrices}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                />
            </div>
        </div>
    );
};

export default Matrices;
